import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { isGcalMultiCalendarEnabled } from "@/lib/gcal-feature-flags";
import {
  loadCalendarFilterState,
  persistCalendarFilterState,
} from "@/lib/appointments-calendar-filter-storage";

interface CalendarFilterable {
  google_calendar_id?: string | null;
}

interface AppointmentsCalendarFilterContextValue {
  hiddenCalendarIds: Set<string>;
  isPanelExpanded: boolean;
  setPanelExpanded: Dispatch<SetStateAction<boolean>>;
  toggleCalendarVisibility: (calendarId: string) => void;
  isCalendarVisible: (calendarId: string) => boolean;
  filterByVisibleCalendars: <T extends CalendarFilterable>(items: T[]) => T[];
  isMultiCalendarEnabled: boolean;
}

const NOOP_VALUE: AppointmentsCalendarFilterContextValue = {
  hiddenCalendarIds: new Set(),
  isPanelExpanded: false,
  setPanelExpanded: () => {},
  toggleCalendarVisibility: () => {},
  isCalendarVisible: () => true,
  filterByVisibleCalendars: (items) => items,
  isMultiCalendarEnabled: false,
};

const AppointmentsCalendarFilterContext =
  createContext<AppointmentsCalendarFilterContextValue | undefined>(undefined);

export function AppointmentsCalendarFilterProvider({
  workspaceId,
  disabled = false,
  children,
}: {
  workspaceId: string | null;
  disabled?: boolean;
  children: ReactNode;
}) {
  const isMultiCalendarEnabled = isGcalMultiCalendarEnabled();
  const [hiddenCalendarIds, setHiddenCalendarIds] = useState<Set<string>>(() => new Set());
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!workspaceId || disabled || !isMultiCalendarEnabled) {
      setHiddenCalendarIds(new Set());
      setIsPanelExpanded(false);
      setHydrated(true);
      return;
    }

    const persisted = loadCalendarFilterState(workspaceId);
    setHiddenCalendarIds(new Set(persisted?.hidden ?? []));
    setIsPanelExpanded(persisted?.expanded ?? false);
    setHydrated(true);
  }, [workspaceId, disabled, isMultiCalendarEnabled]);

  useEffect(() => {
    if (!workspaceId || disabled || !isMultiCalendarEnabled || !hydrated) return;

    persistCalendarFilterState(workspaceId, {
      hidden: Array.from(hiddenCalendarIds),
      expanded: isPanelExpanded,
    });
  }, [
    workspaceId,
    disabled,
    isMultiCalendarEnabled,
    hiddenCalendarIds,
    isPanelExpanded,
    hydrated,
  ]);

  const setPanelExpanded = useCallback((expanded: SetStateAction<boolean>) => {
    setIsPanelExpanded(expanded);
  }, []);

  const toggleCalendarVisibility = useCallback((calendarId: string) => {
    setHiddenCalendarIds((prev) => {
      const next = new Set(prev);
      if (next.has(calendarId)) {
        next.delete(calendarId);
      } else {
        next.add(calendarId);
      }
      return next;
    });
  }, []);

  const isCalendarVisible = useCallback(
    (calendarId: string) => !hiddenCalendarIds.has(calendarId),
    [hiddenCalendarIds],
  );

  const filterByVisibleCalendars = useCallback(
    <T extends CalendarFilterable>(items: T[]): T[] => {
      if (!isMultiCalendarEnabled || hiddenCalendarIds.size === 0) {
        return items;
      }
      return items.filter(
        (item) =>
          !item.google_calendar_id || !hiddenCalendarIds.has(item.google_calendar_id),
      );
    },
    [isMultiCalendarEnabled, hiddenCalendarIds],
  );

  const value = useMemo(
    () => ({
      hiddenCalendarIds,
      isPanelExpanded,
      setPanelExpanded,
      toggleCalendarVisibility,
      isCalendarVisible,
      filterByVisibleCalendars,
      isMultiCalendarEnabled,
    }),
    [
      hiddenCalendarIds,
      isPanelExpanded,
      setPanelExpanded,
      toggleCalendarVisibility,
      isCalendarVisible,
      filterByVisibleCalendars,
      isMultiCalendarEnabled,
    ],
  );

  if (disabled || !isMultiCalendarEnabled) {
    return (
      <AppointmentsCalendarFilterContext.Provider value={NOOP_VALUE}>
        {children}
      </AppointmentsCalendarFilterContext.Provider>
    );
  }

  return (
    <AppointmentsCalendarFilterContext.Provider value={value}>
      {children}
    </AppointmentsCalendarFilterContext.Provider>
  );
}

export function useAppointmentsCalendarFilter(): AppointmentsCalendarFilterContextValue {
  const ctx = useContext(AppointmentsCalendarFilterContext);
  if (ctx === undefined) {
    throw new Error(
      "useAppointmentsCalendarFilter must be used within AppointmentsCalendarFilterProvider",
    );
  }
  return ctx;
}
